import { closeIcon } from "@/assets/icons";
import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

export interface BadgeOption {
  value: string;
  label: string;
  description?: string;
  icon?: string;
  disabled?: boolean;
}

@customElement("sb-badge-selector")
export class BadgeSelector extends LitElement {
  // ---- public properties ----
  @property({ type: Array }) options: BadgeOption[] = [];
  @property({ type: Array }) value: string[] = [];
  @property({ attribute: false }) onChange?: (values: string[]) => void;
  @property({ type: String }) placeholder = "Search roles...";
  @property({ type: String }) label = "";
  @property({ type: Boolean }) disabled = false;
  @property({ type: Number }) maxSelections?: number;
  @property({ type: String }) className = "";

  // ---- internal state ----
  @state() private inputValue = "";
  @state() private isOpen = false;
  @state() private highlightedIndex = -1;

  private blurTimeout: number | null = null;
  private dropdownRef: HTMLElement | null = null;

  protected createRenderRoot() {
    return this; // light DOM
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                           */
  /* ------------------------------------------------------------------ */

  private get filteredOptions(): BadgeOption[] {
    if (!this.inputValue.trim()) return this.options;
    const query = this.inputValue.toLowerCase().trim();
    return this.options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.value.toLowerCase().includes(query),
    );
  }

  private get maxReached(): boolean {
    return (
      this.maxSelections != null && this.value.length >= this.maxSelections
    );
  }

  private isOptionDisabled(opt: BadgeOption): boolean {
    return (
      this.disabled ||
      opt.disabled ||
      (this.maxReached && !this.value.includes(opt.value))
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Event handlers                                                    */
  /* ------------------------------------------------------------------ */

  private handleInput(e: Event) {
    this.inputValue = (e.target as HTMLInputElement).value;
    this.isOpen = true;
    this.highlightedIndex = -1;
  }

  private handleInputClick() {
    // Open dropdown when clicking on the input (even if already focused)
    if (this.disabled || this.maxReached || this.options.length === 0) return;
    if (!this.isOpen) {
      this.isOpen = true;
      this.highlightedIndex = -1;
      this.clearBlurTimeout();
      // Ensure input stays focused
      this.querySelector("input")?.focus();
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (!this.isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        this.isOpen = true;
        this.highlightedIndex = -1;
        e.preventDefault();
        return;
      }
      if (e.key === "Backspace" && !this.inputValue && this.value.length > 0) {
        this.removeLast();
        e.preventDefault();
      }
      return;
    }

    const opts = this.filteredOptions;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.highlightedIndex = Math.min(
          this.highlightedIndex + 1,
          opts.length - 1,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        break;
      case "Enter":
        e.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < opts.length) {
          this.selectOption(opts[this.highlightedIndex]);
        }
        break;
      case "Escape":
        this.isOpen = false;
        this.highlightedIndex = -1;
        break;
      case "Backspace":
        if (!this.inputValue && this.value.length > 0) {
          e.preventDefault();
          this.removeLast();
        }
        break;
      default:
        break;
    }
  }

  private selectOption(option: BadgeOption) {
    if (this.isOptionDisabled(option)) return;

    if (!this.value.includes(option.value)) {
      const newValues = [...this.value, option.value];
      this.value = newValues;
      this.notifyChange(newValues);
    }

    this.inputValue = "";
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  private removeLast() {
    if (this.value.length === 0) return;
    const newValues = this.value.slice(0, -1);
    this.value = newValues;
    this.notifyChange(newValues);
  }

  private removeBadge(valueToRemove: string) {
    const newValues = this.value.filter((v) => v !== valueToRemove);
    this.value = newValues;
    this.notifyChange(newValues);
  }

  private handleFocus() {
    // Show all options when the input is focused (unless disabled)
    if (!this.disabled && this.options.length > 0 && !this.maxReached) {
      this.isOpen = true;
      this.highlightedIndex = -1;
    }
    this.clearBlurTimeout();
  }

  private handleBlur() {
    // Delay to allow click on dropdown items
    this.blurTimeout = window.setTimeout(() => {
      this.isOpen = false;
      this.highlightedIndex = -1;
    }, 200);
  }

  private handleOptionMouseDown(option: BadgeOption) {
    // Prevent blur from closing the dropdown
    this.clearBlurTimeout();
    this.selectOption(option);
  }

  private clearBlurTimeout() {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  }

  private notifyChange(values: string[]) {
    this.onChange?.(values);
    this.dispatchEvent(
      new CustomEvent("badge-selector-change", {
        detail: { values },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */

  render() {
    const isDisabled = this.disabled || this.maxReached;

    return html`
      <div class="sb-space-y-2 ${this.className}">
        ${this.label
          ? html`<rtg-label part="label">${this.label}</rtg-label>`
          : nothing}

        <div
          part="input-wrapper"
          class="${isDisabled ? "sb-cursor-not-allowed sb-opacity-50" : ""}"
        >
          ${this.value.map((v) => {
            const option = this.options.find((o) => o.value === v);
            const label = option?.label ?? v;
            return html`
              <rtg-badge variant="secondary">
                <rtg-typography-small class="sb-truncate"
                  >${label}</rtg-typography-small
                >
                ${!this.disabled
                  ? html`
                      <span
                        class="sb-cursor-pointer sb-ml-1"
                        @click=${() => this.removeBadge(v)}
                      >
                        ${closeIcon()}
                      </span>
                    `
                  : nothing}
              </rtg-badge>
            `;
          })}

          <input
            type="text"
            .value=${this.inputValue}
            placeholder=${this.value.length === 0 ? this.placeholder : ""}
            ?disabled=${isDisabled}
            @input=${this.handleInput}
            @click=${this.handleInputClick}
            @keydown=${this.handleKeyDown}
            @focus=${this.handleFocus}
            @blur=${this.handleBlur}
            class="disabled:sb-cursor-not-allowed"
            autocomplete="off"
            role="combobox"
            aria-expanded=${this.isOpen}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-controls="dropdown-listbox"
          />
        </div>

        ${this.isOpen && this.filteredOptions.length > 0
          ? html`
              <div
                part="dropdown"
                id="dropdown-listbox"
                role="listbox"
                @mousedown=${(e: Event) => e.preventDefault()}
              >
                <ul part="dropdown-list">
                  ${this.filteredOptions.map(
                    (opt, index) => html`
                      <li
                        part="dropdown-item${this.highlightedIndex === index
                          ? " highlighted"
                          : ""}${this.isOptionDisabled(opt) ? " disabled" : ""}"
                        role="option"
                        aria-selected=${this.value.includes(opt.value)}
                        aria-disabled=${this.isOptionDisabled(opt)}
                        class="${this.isOptionDisabled(opt)
                          ? "sb-opacity-50 sb-cursor-not-allowed"
                          : "sb-cursor-pointer"}"
                        @mousedown=${() => this.handleOptionMouseDown(opt)}
                      >
                        <span class="sb-flex-1">${opt.label}</span>
                      </li>
                    `,
                  )}
                </ul>
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-badge-selector": BadgeSelector;
  }
}
